import { useState } from "react";
import { Link } from "react-router-dom";
import { HttpError, request } from "../../../api/http";
import {
  IconClipboardList,
  IconDollar,
  IconFolder,
  IconLink,
  IconQuestion,
} from "../../../components/Icons";
import { useAuth } from "../../../context/AuthContext";
import { AllLeadsRow } from "../../../types/allLeads";
import { LeadStatus } from "../../../types/onboarding";
import AnswersExplorer from "../../LeadDetail/components/AnswersExplorer";
import FileExplorer from "../../OnboardingCustomers/components/FileExplorer";

type Props = {
  row: AllLeadsRow;
};

export default function AllLeadsActions({ row }: Props) {
  const { token } = useAuth();
  const [filesOpen, setFilesOpen] = useState(false);
  const [answersOpen, setAnswersOpen] = useState(false);
  const [otpBusy, setOtpBusy] = useState(false);
  const [otpResult, setOtpResult] = useState<
    | { kind: "ok"; url: string; copied: boolean }
    | { kind: "error"; message: string }
    | null
  >(null);

  const stop = (e: React.MouseEvent) => e.stopPropagation();

  const handleGenerateOtp = async (event: React.MouseEvent) => {
    event.stopPropagation();
    if (otpBusy) return;
    setOtpBusy(true);
    setOtpResult(null);
    try {
      const { url } = await request<{ url: string }>(
        `/api/admin/operations/leads/${row.ulid}/otp-url`,
        { method: "POST", token, body: {} },
      );
      let copied = false;
      try {
        await navigator.clipboard.writeText(url);
        copied = true;
      } catch {
        // clipboard blocked — surface the URL in the toast for manual copy
      }
      setOtpResult({ kind: "ok", url, copied });
    } catch (err) {
      setOtpResult({
        kind: "error",
        message:
          err instanceof HttpError
            ? `HTTP ${err.status}`
            : "No se pudo generar el OTP",
      });
    } finally {
      setOtpBusy(false);
    }
  };

  return (
    <div
      style={{
        display: "flex",
        gap: "0.3rem",
        justifyContent: "flex-end",
        position: "relative",
      }}
      onClick={stop}
    >
      <Link
        to={`/leads/${row.ulid}`}
        title="Abrir detalle del lead"
        style={iconBtnStyle}
      >
        <IconClipboardList />
      </Link>
      <button
        type="button"
        onClick={handleGenerateOtp}
        disabled={otpBusy}
        title={
          otpBusy
            ? "Generando OTP…"
            : "Generar enlace OTP (copia al portapapeles)"
        }
        style={{
          ...iconBtnStyle,
          color: "#0069d9",
          opacity: otpBusy ? 0.6 : 1,
          cursor: otpBusy ? "wait" : "pointer",
        }}
      >
        <IconLink />
      </button>
      <button
        type="button"
        onClick={() => setFilesOpen(true)}
        title="Documentos y biometría del lead"
        style={iconBtnStyle}
      >
        <IconFolder />
      </button>
      <button
        type="button"
        onClick={() => setAnswersOpen(true)}
        title="Cuestionario del lead"
        style={iconBtnStyle}
      >
        <IconQuestion />
      </button>
      {row.subscriptionId ? (
        <Link
          to={`/subscriptions/${row.subscriptionId}`}
          title="Abrir suscripción"
          style={iconBtnStyle}
        >
          <IconDollar />
        </Link>
      ) : (
        <span
          title="Sin suscripción"
          style={{
            ...iconBtnStyle,
            opacity: 0.4,
            cursor: "default",
            pointerEvents: "none",
          }}
        >
          <IconDollar />
        </span>
      )}
      {answersOpen && (
        <AnswersExplorer
          leadUlid={row.ulid}
          onClose={() => setAnswersOpen(false)}
        />
      )}
      {filesOpen && (
        <FileExplorer
          leadId={row.id}
          leadUlid={row.ulid}
          customer={row.fullname || row.email || `Lead ${row.id}`}
          currentStatus={(row.status ?? "") as LeadStatus}
          onStatusChange={() => undefined}
          onClose={() => setFilesOpen(false)}
        />
      )}
      {otpResult && (
        <div
          onClick={(e) => {
            e.stopPropagation();
            setOtpResult(null);
          }}
          title="Cerrar"
          style={{
            position: "absolute",
            top: "calc(100% + 4px)",
            right: 0,
            zIndex: 30,
            background: otpResult.kind === "ok" ? "#e7f1ff" : "#fde2e2",
            color: otpResult.kind === "ok" ? "#0a58ca" : "#b32424",
            padding: "0.45rem 0.7rem",
            borderRadius: "0.45rem",
            fontSize: ".75rem",
            maxWidth: 360,
            boxShadow: "0 4px 10px rgba(0,0,0,.12)",
            cursor: "pointer",
            wordBreak: "break-all",
            whiteSpace: "normal",
          }}
        >
          {otpResult.kind === "ok" ? (
            <>
              <div style={{ fontWeight: 700, marginBottom: "0.2rem" }}>
                {otpResult.copied
                  ? "OTP copiado al portapapeles"
                  : "OTP generado (cópialo manualmente)"}
              </div>
              <a
                href={otpResult.url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                style={{ color: "#0a58ca", textDecoration: "underline" }}
              >
                {otpResult.url}
              </a>
            </>
          ) : (
            otpResult.message
          )}
        </div>
      )}
    </div>
  );
}

const iconBtnStyle: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: "1.9rem",
  height: "1.9rem",
  padding: 0,
  borderRadius: "0.4rem",
  background: "#f8f9fb",
  color: "#1f2933",
  border: "1px solid #dee2e6",
  fontSize: ".95rem",
  cursor: "pointer",
  textDecoration: "none",
};
